import React, { useState, useEffect, useRef, useCallback } from 'react';
import { VehicleRow } from '../types';
import { 
  startProcessingExcelFile, 
  getProcessingStatus, 
  cancelProcessing, 
  downloadProcessedFile,
  ProcessingStatus 
} from '../api/insuranceApi';
import { 
  readVehiclesFromExcel, 
  parseExcelResults, 
  createExcelBlob,
  downloadExcelResults 
} from '../utils/excelUtils';
import { MESSAGES } from '../constants/messages';
import { CONFIG } from '../constants/config';
import FileUpload from './FileUpload';
import Instructions from './Instructions';
import DataTable from './DataTable';
import AdvancedSettings from './AdvancedSettings';
import ProgressDialog from './ProgressDialog';
import ResultDialog from './ResultDialog';
import ErrorDialog from './ErrorDialog';
import ConfirmDialog from './ConfirmDialog';
import Breadcrumb from './Breadcrumb';
import BackgroundIcons from './BackgroundIcons';
import '../styles/Dashboard.css';

const STORAGE_KEY = 'insuranceInfo_state';

interface DashboardProps {
  externalShowInstructions: boolean;
  onInstructionsClosed: () => void;
  onStartFromScratchReady?: (handler: () => void, show: boolean) => void;
  onHideDownloadTemplate?: (hide: boolean) => void;
  onGoHomeReady?: (handler: () => void) => void;
  onProgressDialogChange?: (show: boolean) => void;
  onResultDialogChange?: (show: boolean) => void;
}
interface SavedState {
  vehicles: VehicleRow[];
  delayMs: number;
  showResults: boolean;
  processedCount: number;
  duplicateInfo?: { removed: number; total: number } | null;
}

const Dashboard: React.FC<DashboardProps> = ({ externalShowInstructions, onInstructionsClosed, onStartFromScratchReady, onHideDownloadTemplate, onGoHomeReady, onProgressDialogChange, onResultDialogChange }) => {
  const [vehicles, setVehicles] = useState<VehicleRow[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [delayMs, setDelayMs] = useState<number>(CONFIG.DEFAULT_DELAY_MS);
  const [showProgress, setShowProgress] = useState(false);
  const [progressStatus, setProgressStatus] = useState<ProcessingStatus | null>(null);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [duplicateInfo, setDuplicateInfo] = useState<{ removed: number; total: number } | null>(null);

  // Helper function to create state object
  const createStateObject = (
    vehiclesToSave: VehicleRow[],
    delayToSave?: number,
    showResultsToSave?: boolean,
    processedCountToSave?: number,
    duplicateInfoToSave?: { removed: number; total: number } | null
  ): SavedState => ({
    vehicles: vehiclesToSave,
    delayMs: delayToSave ?? delayMs,
    showResults: showResultsToSave ?? showResults,
    processedCount: processedCountToSave ?? processedCount,
    duplicateInfo: duplicateInfoToSave !== undefined ? duplicateInfoToSave : duplicateInfo,
  });

  // Helper function to save state to localStorage
  const saveStateToLocalStorage = (
    vehiclesToSave: VehicleRow[], 
    delayToSave?: number,
    showResultsToSave?: boolean,
    processedCountToSave?: number,
    duplicateInfoToSave?: { removed: number; total: number } | null
  ) => {
    try {
      if (vehiclesToSave.length > 0) {
        const stateToSave = createStateObject(vehiclesToSave, delayToSave, showResultsToSave, processedCountToSave, duplicateInfoToSave);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      handleStorageError(error, vehiclesToSave, delayToSave, showResultsToSave, processedCountToSave, duplicateInfoToSave);
    }
  };

  // Helper function to handle storage errors
  const handleStorageError = (
    error: unknown,
    vehiclesToSave: VehicleRow[],
    delayToSave?: number,
    showResultsToSave?: boolean,
    processedCountToSave?: number,
    duplicateInfoToSave?: { removed: number; total: number } | null
  ) => {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn('LocalStorage quota exceeded. Clearing old data and retrying...');
        try {
          localStorage.removeItem(STORAGE_KEY);
          if (vehiclesToSave.length > 0) {
          const stateToSave = createStateObject(vehiclesToSave, delayToSave, showResultsToSave, processedCountToSave, duplicateInfoToSave);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
          }
        } catch (retryError) {
          console.error('Error saving state after retry:', retryError);
        }
      } else {
        console.error('Error saving state:', error);
      }
  };

  // Helper function to normalize vehicle data
  const normalizeVehicle = (v: VehicleRow): VehicleRow => ({
            vatNumber: v.vatNumber || '',
            licensePlate: v.licensePlate || '',
            status: v.status || undefined,
            searchDate: v.searchDate || undefined,
            vehicleType: v.vehicleType || undefined,
            insuranceCompany: v.insuranceCompany || undefined,
            coverageStart: v.coverageStart || undefined,
            coverageEnd: v.coverageEnd || undefined,
            message: v.message || undefined,
  });
          
  // Helper function to restore state from parsed data
  const restoreStateFromParsed = (parsed: SavedState) => {
    if (parsed.vehicles && parsed.vehicles.length > 0) {
      const restoredVehicles = parsed.vehicles.map(normalizeVehicle);
          setVehicles(restoredVehicles);
        setDelayMs(parsed.delayMs ?? CONFIG.DEFAULT_DELAY_MS);
        setShowResults(parsed.showResults || false);
        setProcessedCount(parsed.processedCount || 0);
          setDuplicateInfo(parsed.duplicateInfo || null);
        }
  };

  // Helper function to clear corrupted localStorage
  const clearCorruptedStorage = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (clearError) {
      console.error('Error clearing corrupted state:', clearError);
    }
  };

  // Load state from localStorage on mount
  useEffect(() => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        const parsed: SavedState = JSON.parse(savedState);
        restoreStateFromParsed(parsed);
      }
    } catch (error) {
      console.error('Error loading saved state:', error);
      clearCorruptedStorage();
    }
  }, []);

  // Save state to localStorage whenever key values change
  useEffect(() => {
    saveStateToLocalStorage(vehicles, delayMs, showResults, processedCount, duplicateInfo);
  }, [vehicles, delayMs, showResults, processedCount, duplicateInfo]);

  // Notify App when progress dialog changes
  useEffect(() => {
    onProgressDialogChange?.(showProgress);
  }, [showProgress, onProgressDialogChange]);

  // Notify App when result dialog changes
  useEffect(() => {
    onResultDialogChange?.(showResults);
  }, [showResults, onResultDialogChange]);

  useEffect(() => {
    if (externalShowInstructions) {
      setShowInstructions(true);
    }
  }, [externalShowInstructions]);

  const handleFileUpload = async (file: File) => {
    try {
      const vehicleData = await readVehiclesFromExcel(file);
      const totalCount = vehicleData.length;
      
      // Remove duplicates based on AFM (vatNumber) and license plate
      const seen = new Set<string>();
      const uniqueVehicles: VehicleRow[] = [];
      const duplicates: VehicleRow[] = [];
      
      vehicleData.forEach((vehicle) => {
        const key = `${vehicle.vatNumber}-${vehicle.licensePlate}`.toLowerCase().trim();
        if (seen.has(key)) {
          duplicates.push(vehicle);
        } else {
          seen.add(key);
          uniqueVehicles.push(vehicle);
        }
      });
      
      const removedCount = duplicates.length;
      
      setVehicles(uniqueVehicles);
      setShowInstructions(false);
      
      // Set duplicate info if duplicates were removed
      const newDuplicateInfo = removedCount > 0 ? {
        removed: removedCount,
        total: totalCount
      } : null;
      setDuplicateInfo(newDuplicateInfo);
      
      // Save state immediately after file upload
      saveStateToLocalStorage(uniqueVehicles, delayMs, false, 0, newDuplicateInfo);
    } catch (error) {
      console.error('Error reading file:', error);
      setErrorMessage(MESSAGES.ERROR_READING_FILE);
      setDuplicateInfo(null);
    }
  };

  const processVehicles = async () => {
    // Process all unprocessed vehicles
    const unprocessedVehicles = vehicles.filter(v => !v.status);
    
    if (unprocessedVehicles.length === 0) {
      setErrorMessage('Δεν υπάρχουν οχήματα προς επεξεργασία');
      return;
    }
    
    // Validate that all vehicles have both AFM and license plate
    const invalidVehicles = unprocessedVehicles.filter(v => 
      !v.vatNumber || !v.vatNumber.trim() || !v.licensePlate || !v.licensePlate.trim()
    );
    
    if (invalidVehicles.length > 0) {
      setErrorMessage(`Παρακαλώ συμπληρώστε το ΑΦΜ και τον Αριθμό Κυκλοφορίας για όλα τα οχήματα. Βρέθηκαν ${invalidVehicles.length} οχήματα με ελλιπή δεδομένα.`);
      return;
    }

    setIsProcessing(true);
    setProcessedCount(0);
    setShowProgress(true);

    try {
      // Create Excel blob from all unprocessed vehicles
      const blob = createExcelBlob(unprocessedVehicles);
      
      // Start processing and get jobId
      const jobId = await startProcessingExcelFile(blob, delayMs);
      setCurrentJobId(jobId);
      
      // Start polling for status
      startPolling(jobId);
    } catch (error) {
      console.error('Error starting processing:', error);
      setErrorMessage(MESSAGES.ERROR_PROCESSING_VEHICLES);
      setIsProcessing(false);
      setShowProgress(false);
    }
  };

  // Helper function to check if a field has a value
  const hasFieldValue = (value: string | undefined | null): boolean => {
    return value !== undefined && value !== null && value.trim() !== '';
  };

  // Helper function to merge vehicle with result
  const mergeVehicleWithResult = (vehicle: VehicleRow, result: VehicleRow): VehicleRow => {
    return {
      ...vehicle,
      status: hasFieldValue(result.status) ? result.status : vehicle.status,
      searchDate: hasFieldValue(result.searchDate) ? result.searchDate : vehicle.searchDate,
      vehicleType: hasFieldValue(result.vehicleType) ? result.vehicleType : vehicle.vehicleType,
      insuranceCompany: hasFieldValue(result.insuranceCompany) ? result.insuranceCompany : vehicle.insuranceCompany,
      coverageStart: hasFieldValue(result.coverageStart) ? result.coverageStart : vehicle.coverageStart,
      coverageEnd: hasFieldValue(result.coverageEnd) ? result.coverageEnd : vehicle.coverageEnd,
      message: hasFieldValue(result.message) ? result.message : vehicle.message,
    };
  };

  // Helper function to merge vehicles with results
  const mergeVehiclesWithResults = (currentVehicles: VehicleRow[], updatedVehicles: VehicleRow[]): VehicleRow[] => {
    const mergedVehicles = currentVehicles.map((vehicle) => {
      const matchingResult = updatedVehicles.find(
        result => 
          result.vatNumber === vehicle.vatNumber && 
          result.licensePlate === vehicle.licensePlate
      );
      
      if (matchingResult) {
        return mergeVehicleWithResult(vehicle, matchingResult);
      }
      return vehicle;
    });
    
    return mergedVehicles.map(v => ({ ...v }));
  };

  // Helper function to handle completed job
  const handleCompletedJob = async (jobId: string) => {
    try {
      const resultBlob = await downloadProcessedFile(jobId);
      const updatedVehicles = await parseExcelResults(resultBlob);
      
      stopPolling();
      
      const newProcessedCount = updatedVehicles.length;
      
      setVehicles((currentVehicles) => mergeVehiclesWithResults(currentVehicles, updatedVehicles));
      
      setIsProcessing(false);
      setShowProgress(false);
      setProcessedCount(newProcessedCount);
      setShowResults(true);
    } catch (error) {
      console.error('Error downloading results:', error);
      setErrorMessage('Αποτυχία λήψης αποτελεσμάτων');
      setIsProcessing(false);
      setShowProgress(false);
    }
  };

  // Helper function to handle cancelled job
  const handleCancelledJob = () => {
    setIsProcessing(false);
    setShowProgress(false);
    setErrorMessage('Η επεξεργασία ακυρώθηκε');
  };

  // Helper function to handle error status
  const handleErrorStatus = (error: string) => {
    setIsProcessing(false);
    setShowProgress(false);
    setErrorMessage(error);
  };

  // Helper function to handle polling error
  const handlePollingError = (error: unknown) => {
    console.error('Error polling status:', error);
    stopPolling();
    setIsProcessing(false);
    setShowProgress(false);
    setErrorMessage('Αποτυχία παρακολούθησης προόδου');
  };

  const startPolling = (jobId: string) => {
    const poll = async () => {
      try {
        const status = await getProcessingStatus(jobId);
        setProgressStatus(status);
        
        if (status.completed || status.cancelled) {
          stopPolling();
          
          if (status.cancelled) {
            handleCancelledJob();
          } else if (status.error) {
            handleErrorStatus(status.error);
          } else {
            await handleCompletedJob(jobId);
          }
        }
      } catch (error) {
        handlePollingError(error);
      }
    };

    poll();
    pollingIntervalRef.current = setInterval(poll, 100);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const handleCancelProcessing = async () => {
    if (currentJobId) {
      try {
        await cancelProcessing(currentJobId);
        stopPolling();
        setIsProcessing(false);
        setShowProgress(false);
        setErrorMessage('Η επεξεργασία ακυρώθηκε');
      } catch (error) {
        console.error('Error cancelling:', error);
        setErrorMessage('Αποτυχία ακύρωσης');
      }
    }
  };

  const downloadResults = () => {
    downloadExcelResults(vehicles);
  };

  const reset = () => {
    stopPolling();
    setVehicles([]);
    setShowInstructions(false);
    setShowResults(false);
    setProcessedCount(0);
    setShowProgress(false);
    setProgressStatus(null);
    setCurrentJobId(null);
    setDuplicateInfo(null);
    // Clear localStorage
    localStorage.removeItem(STORAGE_KEY);
  };

  const performReset = useCallback(() => {
    reset();
    setShowInstructions(false);
  }, []);

  const handleGoHome = useCallback(() => {
    if (vehicles.length > 0) {
      setPendingAction(() => () => {
        performReset();
      });
      setShowConfirmDialog(true);
    } else {
      performReset();
    }
  }, [vehicles.length, performReset]);

  const handleStartFromScratch = useCallback(() => {
    if (vehicles.length > 0) {
      setPendingAction(() => () => {
        performReset();
      });
      setShowConfirmDialog(true);
    } else {
      performReset();
    }
  }, [vehicles.length, performReset]);

  const handleConfirm = () => {
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
    setShowConfirmDialog(false);
  };

  const handleCancel = () => {
    setPendingAction(null);
    setShowConfirmDialog(false);
  };

  // Notify App when startFromScratch handler is ready
  useEffect(() => {
    if (onStartFromScratchReady) {
      onStartFromScratchReady(handleStartFromScratch, vehicles.length > 0);
    }
  }, [vehicles.length, onStartFromScratchReady, handleStartFromScratch]);

  // Notify App to hide download template button when in dashboard
  useEffect(() => {
    if (onHideDownloadTemplate) {
      onHideDownloadTemplate(vehicles.length > 0);
    }
  }, [vehicles.length, onHideDownloadTemplate]);

  // Notify App when goHome handler is ready
  useEffect(() => {
    if (onGoHomeReady) {
      onGoHomeReady(handleGoHome);
    }
  }, [onGoHomeReady, handleGoHome]);

  const handleEditVehicle = (index: number, field: 'vatNumber' | 'licensePlate', value: string) => {
    // Only allow editing if vehicle hasn't been processed yet
    if (vehicles[index]?.status) {
      return;
    }
    
    setVehicles((currentVehicles) => {
      const updated = [...currentVehicles];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      return updated;
    });
  };

  const handleAddRow = () => {
    const newVehicle: VehicleRow = {
      vatNumber: '',
      licensePlate: ''
    };
    
    setVehicles((currentVehicles) => {
      return [...currentVehicles, newVehicle];
    });
  };

  const handleDeleteRow = (index: number) => {
    // Only allow deleting if vehicle hasn't been processed yet
    if (vehicles[index]?.status) {
      return;
    }
    
    setVehicles((currentVehicles) => {
      return currentVehicles.filter((_, i) => i !== index);
    });
  };

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const handleCloseInstructions = () => {
    setShowInstructions(false);
    onInstructionsClosed();
  };

  return (
    <>
    <div className="dashboard">
      <BackgroundIcons />
      <div className="dashboard-container">
        {vehicles.length > 0 && (
          <Breadcrumb 
            onGoHome={handleGoHome}
            showUploadNew={vehicles.length > 0}
          />
        )}
        
        {showInstructions && (
          <Instructions onClose={handleCloseInstructions} />
        )}
        
        {vehicles.length === 0 ? (
          <FileUpload onFileUpload={handleFileUpload} />
        ) : (
          <>
            <AdvancedSettings 
              delayMs={delayMs}
              onDelayChange={setDelayMs}
            />
            <DataTable 
              vehicles={vehicles}
              isProcessing={isProcessing}
              onProcess={processVehicles}
              onDownload={downloadResults}
              onReset={reset}
              onEditVehicle={handleEditVehicle}
              onAddRow={handleAddRow}
              onDeleteRow={handleDeleteRow}
              duplicateInfo={duplicateInfo}
            />
            
            {showResults && (
              <ResultDialog
                totalProcessed={processedCount}
                onClose={() => setShowResults(false)}
                onDownload={downloadResults}
              />
            )}
          </>
        )}

        {showProgress && progressStatus && (
          <ProgressDialog
            current={progressStatus.processed}
            total={progressStatus.total}
            onCancel={handleCancelProcessing}
            isProcessing={isProcessing && !progressStatus.completed && !progressStatus.cancelled}
          />
        )}

        {errorMessage && (
          <ErrorDialog
            title="Σφάλμα"
            message={errorMessage}
            onClose={() => setErrorMessage(null)}
          />
        )}

      </div>
    </div>

    {showConfirmDialog && (
      <ConfirmDialog
        title="Επιβεβαίωση"
        message="Είστε σίγουροι ότι θέλετε να επιστρέψετε στην αρχική σελίδα; Όλα τα τρέχοντα δεδομένα και η προόδος θα χαθούν."
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        confirmText="Ναι, Επιστροφή"
        cancelText="Άκυρο"
      />
    )}
    </>
  );
};

export default Dashboard;
